import { useCallback, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Heading } from "components/Heading";
import { ErrorSummary } from "components/ErrorSummary";
import { JsonForm } from "components/JsonForm";
import { usePasswordRequirements, userResetPassword } from "components/utils";
import { Container } from "components/Layout/Container";
import { usePageTopAndBottomRows } from "components/utils/page";
import { PageComponentsList } from ".";
import { Card } from "react-bootstrap";
import { HeadingLevel } from "../../types/HeadingLevel";
import { Icon } from "../Icon";
import { SuccessCardPage } from "./SuccessCardPage";
import { PageLink } from "../App/utils";
import IconInputWidget from "../JsonForm/theme/widgets/IconInputWidget";

export function ResetPasswordPage() {
    const history = useHistory();
    const submitted = history?.location?.state?.submitted === true;
    const pathParts = (window.location.pathname ?? "").split("/");
    const customerNumber = pathParts[2];
    const temporaryPassword = pathParts[3];
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorSummary, setErrorSummary] = useState(undefined);
    const { topRows, bottomRows } = usePageTopAndBottomRows();
    const [passwordRequirements, isLoading] = usePasswordRequirements();
    const passwordHelpText = !isLoading && passwordRequirements ? passwordRequirements : undefined;
    const widgets = { iconInput: IconInputWidget };
    const transformErrors = (errors) =>
        errors.map((e) => (e.name === "required" ? { ...e, message: "Please fill out this field." } : e));

    const schema = {
        type: "object",
        required: ["newPassword", "repeatedPassword"],
        properties: {
            newPassword: {
                type: "string",
                title: "New Password",
            },
            repeatedPassword: {
                type: "string",
                title: "Re-Enter Password",
            },
        },
    };

    const uiSchema = {
        newPassword: {
            "ui:widget": "iconInput",
            "ui:options": { icon: "lock", inputType: "password", helpIcon: true, autoComplete: "new-password" },
            "ui:help": passwordHelpText,
            "ui:placeholder": "Enter your new password",
            "ui:autofocus": true,
        },
        repeatedPassword: {
            "ui:widget": "iconInput",
            "ui:options": { icon: "lock", inputType: "password", autoComplete: "new-password" },
            "ui:placeholder": "Re-Enter your password",
        },
    };

    function validatePassword(formData, errors) {
        if (formData.repeatedPassword && formData.newPassword !== formData.repeatedPassword) {
            errors.repeatedPassword.addError("Passwords don't match");
        }
        return errors;
    }

    const onSubmit = useCallback(
        ({ formData }) => {
            setErrorSummary(undefined);
            setIsSubmitting(true);

            userResetPassword({
                id: customerNumber,
                temporaryPassword: temporaryPassword,
                newPassword: formData.newPassword,
            })
                .then(() => history.replace({ pathname: PageLink.CustomerReset, state: { submitted: true } }))
                .catch((error) => {
                    setErrorSummary(error);
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        },
        [history, customerNumber, temporaryPassword]
    );

    if (submitted) {
        return (
            <SuccessCardPage
                icon="shield-check"
                title="Password successfully changed"
                text="Your password has been updated successfully. You are now signed in with your new password."
                buttonText="Back to My Applications"
                to={PageLink.CustomerHome}
            />
        );
    }

    return (
        <>
            <PageComponentsList components={topRows} />
            <Container style={{ maxWidth: 480 }} className="mt-6 mb-6">
                <Card className="p-2 rounded-top-4 rounded-bottom-4 border-0 shadow-sm">
                    <Card.Body>
                        <Link
                            to={PageLink.SignIn}
                            variant="ghost"
                            className="d-inline-block mb-4 text-portal-primary text-decoration-none py-1 px-2"
                        >
                            <Icon icon={["fal", "arrow-left"]} className="me-2" />
                            Back to Sign In
                        </Link>
                        <Container className="p-0 d-flex flex-column align-items-center">
                            <Icon icon={["far", "shield-check"]} className="fs-1 text-secondary" />
                            <Heading level={HeadingLevel.H5} className="fw-medium m-0 mt-2">
                                Reset Password
                            </Heading>
                            <p className="text-secondary text-center mt-1">Create a secure new password to protect your account.</p>
                        </Container>
                        <Container className="p-0 d-flex flex-column align-items-center">
                            <ErrorSummary errorSummary={errorSummary} />
                            <JsonForm
                                widgets={widgets}
                                schema={schema}
                                uiSchema={uiSchema}
                                validate={validatePassword}
                                onSubmit={onSubmit}
                                isSubmitting={isSubmitting}
                                transformErrors={transformErrors}
                                buttonClassName="d-grid mt-3"
                            ></JsonForm>
                        </Container>
                    </Card.Body>
                </Card>
            </Container>
            <PageComponentsList components={bottomRows} />
        </>
    );
}
